import React, { useState, useEffect, useCallback } from 'react';
import { Card, Spinner, Modal, Button } from 'react-bootstrap';
import { weatherService } from '../services/weatherService';
import { FaSyncAlt } from 'react-icons/fa';

export function WeatherWidget({ latitude, longitude, className = '', userId = null }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const fetchWeather = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const weatherData = await weatherService.getCurrentWeather(latitude, longitude, userId);
      setWeather(weatherData);
    } catch (err) {
      console.error('Failed to fetch weather:', err);
      setError('Weather unavailable');
    } finally {
      setLoading(false);
    }
  }, [latitude, longitude, userId]);

  useEffect(() => {
    if (latitude && longitude) {
      // Check if weather has been loaded for this user session
      if (userId) {
        const weatherLoaded = weatherService.hasWeatherLoaded(userId);
        setHasLoaded(weatherLoaded);
        
        // Only fetch weather automatically if it hasn't been loaded yet
        if (!weatherLoaded) {
          fetchWeather();
          // Mark weather as loaded for this session
          weatherService.markWeatherLoaded(userId);
        }
      } else {
        // No user ID, fetch weather normally
        fetchWeather();
      }
    }
  }, [latitude, longitude, userId, fetchWeather]);

  const handleManualRefresh = async () => {
    if (userId) {
      // Reset weather loaded state for manual refresh
      weatherService.resetWeatherLoaded(userId);
      setHasLoaded(false);
    }
    await fetchWeather();
    if (userId) {
      weatherService.markWeatherLoaded(userId);
      setHasLoaded(true);
    }
  };

  if (!latitude || !longitude) {
    return null;
  }

  if (loading) {
    return (
      <Card className={`weather-widget ${className}`}>
        <Card.Body className="p-2 text-center">
          <Spinner animation="border" size="sm" />
          <small className="ms-2">Loading weather...</small>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`weather-widget ${className}`}>
        <Card.Body className="p-2 text-center">
          <small className="text-muted">{error}</small>
        </Card.Body>
      </Card>
    );
  }

  // If no weather data and we haven't loaded yet, show nothing
  if (!weather && !hasLoaded) {
    return null;
  }

  // If we have weather data, show it with click to view details
  if (weather) {
    const weatherIcon = weatherService.getWeatherIcon(weather.icon);
    const weatherDescription = weatherService.getWeatherDescription(weather.description);
    const windDirection = weatherService.getWindDirection(weather.wind_direction);

    return (
      <>
        <button
          type="button"
          className={`weather-chip-round ${className}`}
          onClick={() => setShowDetails(true)}
          aria-label={`Weather: ${weather.temperature} degrees, wind ${weather.wind_speed} ${windDirection}`}
        >
          <span className="weather-icon-sm">
            {weatherIcon}
          </span>
          <span className="weather-temp-line">
            {weather.temperature}°
          </span>
          <span className="weather-wind-line">
            {weather.wind_speed} {windDirection}
          </span>
        </button>

        {/* Weather Details Modal */}
        <Modal 
          show={showDetails} 
          onHide={() => setShowDetails(false)}
          size="md"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Weather Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center mb-3">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <span className="weather-icon me-3" style={{ fontSize: '2rem' }}>
                  {weatherIcon}
                </span>
                <div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>
                    {weather.temperature}°
                  </div>
                  <div style={{ fontSize: '1rem', color: 'var(--text-secondary)' }}>
                    {weatherDescription}
                  </div>
                </div>
              </div>
              <div className="row text-start">
                <div className="col-6">
                  <strong>Feels like:</strong> {weather.feels_like}°
                </div>
                <div className="col-6">
                  <strong>Humidity:</strong> {weather.humidity}%
                </div>
                <div className="col-6">
                  <strong>Wind:</strong> {weather.wind_speed} mph
                </div>
                <div className="col-6">
                  <strong>Direction:</strong> {windDirection}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-primary"
              onClick={handleManualRefresh}
              disabled={loading}
            >
              <FaSyncAlt className="me-2" />
              {loading ? 'Refreshing...' : 'Refresh Weather'}
            </Button>
            <Button variant="secondary" onClick={() => setShowDetails(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  // If we've loaded weather before but don't have current data, show refresh button
  if (hasLoaded && !weather) {
    return (
      <Card 
        className={`weather-widget ${className}`}
        style={{ cursor: 'pointer' }}
        onClick={handleManualRefresh}
        title="Click to refresh weather"
      >
        <Card.Body className="p-2 text-center">
          <Button
            variant="link"
            className="p-0 text-decoration-none"
            disabled={loading}
            title="Refresh weather"
          >
            <FaSyncAlt className="text-muted" style={{ fontSize: '1.2rem' }} />
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return null;
}
